import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';


function MirArmTherapy() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const features = [
        {
            icon: "dvr",
            title: "AR + Mirror Therapy",
            description: "Combines the principle of mirror therapy with augmented reality, reflecting healthy arm movements as the affected arm."
        },
        {
            icon: "touch_app", // Changed from touch_app to back_hand in mind, but touch_app is safe. User image had issue with do_touch. 
            title: "Contactless Interaction",
            description: "Sensor-based, contactless infrared hand-arm movement detection using Leap Motion technology."
        },
        {
            icon: "accessibility_new",
            title: "High Accessibility",
            description: "Suitable for patients with low levels of motor recovery; does not require active paretic arm movement."
        },
        {
            icon: "psychology",
            title: "Neuroplasticity",
            description: "Leverages visual feedback and action observation to stimulate neuroplasticity and motor recovery."
        },
        {
            icon: "sports_esports",
            title: "Gamified Training",
            description: "Supports repetitive motor training through engaging, task-oriented game mechanics."
        },
        {
            icon: "medical_services",
            title: "Clinical Utility",
            description: "Low-cost, portable, and designed for independent use in clinical settings as a complementary approach."
        }
    ];

    const roles = [
        "Designing the augmented reality-based rehabilitation system architecture",
        "Developing Unity-based desktop applications",
        "Integrating Leap Motion sensors and processing real-time motion data",
        "Developing software for task-oriented and gamified therapy modules",
        "Implementing interaction, feedback, and data collection mechanisms",
        "Working with the physiotherapy and rehabilitation team to translate clinical requirements into software"
    ];

    return (
        <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-slate-900 text-white">
            <div className="fixed inset-0 bg-[url('https://images.unsplash.com/photo-1552862750-746b8f6f7f25?q=80&w=2670&auto=format&fit=crop')] bg-cover bg-center opacity-10 pointer-events-none"></div>
            <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-900/95 to-slate-900/80 pointer-events-none"></div>

            <div className="relative z-10 flex flex-col min-h-screen w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
                    <Link to="/experience" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                        <span className="material-symbols-outlined">arrow_back</span>
                        <span className="font-bold uppercase tracking-wider text-sm">Back to Experience</span>
                    </Link>
                    <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 self-start sm:self-auto">
                        <span className="material-symbols-outlined text-sm">sports_esports</span>
                        <span className="text-xs font-bold uppercase">Gamified Therapy</span>
                    </div>
                </div>

                <div className="flex-1 flex flex-col items-center w-full pb-10">
                    {/* Header Section */}
                    <div className="text-center max-w-4xl mx-auto mb-16">
                        <h1 className="text-5xl sm:text-6xl md:text-8xl font-black tracking-tight mb-6 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 animate-fade-in-up">
                            MirrARm Therapy
                        </h1>
                        <div className="inline-block border-b-4 border-cyan-500 pb-2 mb-8">
                            <p className="text-xl sm:text-2xl md:text-3xl text-slate-300 font-light break-words">
                                Augmented Reality-Based Mirror Therapy System
                            </p>
                        </div>
                        <p className="text-slate-400 text-lg sm:text-xl leading-relaxed max-w-3xl mx-auto mb-12">
                            <strong>MirrAR</strong> is an innovative rehabilitation system developed to support upper extremity recovery after stroke. By detecting the patient's healthy arm movements in real-time and visually reflecting them as the affected arm, it creates a powerful illusion that drives neuroplasticity.
                        </p>
                        <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-700/50">
                            <img src="/assets/projects/mirrARM.png" alt="MirrARm Therapy System Interface" className="w-full h-auto" />
                        </div>
                    </div>

                    {/* Features Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full max-w-6xl mb-16">
                        {features.map((feature, index) => (
                            <div key={index} className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700/50 hover:bg-slate-800 hover:border-cyan-500/30 transition-all group">
                                <span className="material-symbols-outlined text-4xl text-cyan-400 mb-4 group-hover:scale-110 transition-transform">{feature.icon}</span>
                                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                                <p className="text-slate-400 leading-relaxed text-sm">
                                    {feature.description}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Technical Implementation Section */}
                    <div className="w-full max-w-5xl bg-slate-800/30 rounded-3xl p-8 sm:p-12 border border-slate-700/50 backdrop-blur-sm mb-12">
                        <h3 className="text-2xl sm:text-3xl font-bold text-white mb-8 flex items-center gap-4 border-b border-slate-700 pb-6">
                            <span className="material-symbols-outlined text-cyan-400 text-3xl sm:text-4xl">code</span>
                            Technical Implementation
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                            <div className="space-y-6">
                                <div className="flex gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center shrink-0 text-cyan-400">
                                        <span className="material-symbols-outlined text-2xl">view_in_ar</span>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white text-lg mb-1">Unity Engine</h4>
                                        <p className="text-slate-400 text-sm leading-relaxed">Built with <strong>Unity</strong> for high-fidelity rendering and physics-based interactions.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center shrink-0 text-cyan-400">
                                        <span className="material-symbols-outlined text-2xl">back_hand</span>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white text-lg mb-1">Leap Motion Tracking</h4>
                                        <p className="text-slate-400 text-sm leading-relaxed">Utilizes official SDKs for precise, millisecond-latency 3D hand and finger tracking without wearables.</p>
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-6">
                                <div className="flex gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center shrink-0 text-cyan-400">
                                        <span className="material-symbols-outlined text-2xl">grid_on</span>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white text-lg mb-1">Physics Interactions</h4>
                                        <p className="text-slate-400 text-sm leading-relaxed">Powered by NVIDIA PhysX (RigidBody, Colliders) to simulate realistic weight, gravity, and collision responses.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center shrink-0 text-cyan-400">
                                        <span className="material-symbols-outlined text-2xl">architecture</span>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white text-lg mb-1">Modular C# Architecture</h4>
                                        <p className="text-slate-400 text-sm leading-relaxed">Scalable MonoBehaviour-based system allowing for easy addition of new therapeutic exercises and parameters.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* My Role & Contribution Section */}
                    <div className="w-full max-w-5xl">
                        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-3xl p-8 sm:p-12 border border-slate-700/50 backdrop-blur-sm">
                            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6 flex items-center gap-4 border-b border-slate-700 pb-6">
                                <span className="material-symbols-outlined text-cyan-400 text-3xl sm:text-4xl">person_check</span>
                                My Role & Contribution
                            </h3>
                            <p className="text-slate-300 text-lg mb-8 leading-relaxed">
                                In this project, as a computer engineer, I actively participated in the process from the technical design to the implementation of the MirrAR system.
                            </p>
                            <ul className="space-y-4">
                                {roles.map((role, index) => (
                                    <li key={index} className="flex gap-4 items-start">
                                        <span className="material-symbols-outlined text-cyan-500 shrink-0 mt-0.5">check_circle</span>
                                        <span className="text-slate-300 text-base sm:text-lg leading-relaxed">{role}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="mt-12 p-4 rounded-lg bg-cyan-900/10 border border-cyan-500/20 text-cyan-300/70">
                        <p className="font-mono text-xs sm:text-sm tracking-wider uppercase flex flex-col sm:flex-row items-center gap-2 text-center">
                            <span className="material-symbols-outlined text-lg">info</span>
                            Project is currently in active development
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MirArmTherapy;
